import { path, piped, reduce } from 'rambdax';

export type CpUserModals = {
  readonly showNeedVerification: boolean;
  readonly _type: '_cpUserModals';
};

export type CpUserWalletInfo = {
  readonly realBalance: number;
  readonly totalBalance: number;
  readonly toPayout: number;
  readonly bonusBalance: number;
  readonly bonusWagered: number;
  readonly bonusWageringLeft: number;
  readonly bonusRedemption: number;
  readonly bonusBalanceSum: number;
  readonly savedWin: number;
  readonly bonusQueueSum: number;
  readonly currency: string;
  readonly _type: '_cpUserWalletInfo';
};

export type CpUserInfoDetails = {
  readonly firstAddress: string;
  readonly secondaryAddress: string;
  readonly birthDate: Date;
  readonly city: string;
  readonly country: string;
  readonly currency: string;
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly login: string;
  readonly isMale: boolean;
  readonly isFemale: boolean;
  readonly nationalIdNumber: string;
  readonly bankAccountNumber: string | undefined; // with country code
  readonly awaitingWithdrawals: number;
  readonly postalCode: number;
  readonly isShowPrivateData: boolean;
  readonly telephone: string;
  readonly isVerificated: boolean;
  readonly walletInfo: CpUserWalletInfo;
  readonly walletId: string;
  readonly userModals: CpUserModals;
  readonly _type: '_cpUserInfoDetails';
};

function createCpUserModals(json: Record<string, any>): CpUserModals {
  return {
    showNeedVerification: json['verificated'] === false,
    _type: '_cpUserModals'
  };
}

function createCpUserWalletInfo(json: Record<string, any>): CpUserWalletInfo {
  return {
    realBalance: json['balance'],
    totalBalance: json['totalBalance'],
    toPayout: json['withdrawableBalance'],
    bonusBalance: json['bonusBalance'],
    bonusWagered: json['bonusTurnoverSum'],
    bonusWageringLeft: json['bonusLeftToWager'],
    bonusRedemption: json['bonusRedemption'],
    bonusBalanceSum: json['bonusBalanceRealPart'] + json['bonusBalanceBonusPart'],
    savedWin: json['savedWin'] ?? 0,
    bonusQueueSum: piped(
      json['bonusQueueSum'].map(path('value')) as number[],
      reduce((sum: number, bonusValue: number) => sum + bonusValue, 0)
    ),
    currency: json['currency'],
    _type: '_cpUserWalletInfo'
  };
}

export function createCpUserInfoDetailsFromHttpJson(json: Record<string, any>): CpUserInfoDetails {
  return {
    firstAddress: json['address_1'],
    secondaryAddress: json['address_2'],
    birthDate: new Date(Date.parse(json['birth_date'])),
    city: json['city'],
    country: json['country'],
    currency: json['currency'],
    email: json['email'],
    firstName: json['first_name'],
    lastName: json['last_name'],
    login: json['login'],
    isMale: json['gender'] == 'm',
    isFemale: json['gender'] == 'f',
    nationalIdNumber: json['national_id_number'],
    bankAccountNumber: json['payment']?.['bank_account'] ? json['payment']['bank_account'] : undefined,
    awaitingWithdrawals: json['payment']?.['transaction_value__sum'],
    postalCode: json['postal_code'],
    isShowPrivateData: json['show_private_data'],
    telephone: json['telephone'],
    isVerificated: json['verificated'],
    walletInfo: createCpUserWalletInfo(json['wallet']),
    walletId: json['wallet_id'],
    userModals: createCpUserModals(json),
    _type: '_cpUserInfoDetails'
  };
}

export function createTestingCpUserInfoDetailsPartial(partial: Partial<CpUserInfoDetails>): CpUserInfoDetails {
  return {
    ...partial,
    _type: '_cpUserInfoDetails'
  } as CpUserInfoDetails;
}

export function createTestingCpUserWalletInfoPartial(partial: Partial<CpUserWalletInfo>): CpUserWalletInfo {
  return {
    ...partial,
    _type: '_cpUserWalletInfo'
  } as CpUserWalletInfo;
}
